import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { CurrentStaffAccount } from '@garage/shared'
import { Button, TextField } from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { AuthBackground } from '@/components/AuthBackground.js'

/**
 * Blocks the rest of the app until a temporary password (set by an admin on
 * account creation or a reset) is replaced with one only this staff member
 * knows. Shown by AuthGate whenever the signed-in account has
 * `mustChangePassword` set — there is no way to skip it.
 */
export function ChangePasswordRequiredPage({
  account,
  onChanged,
}: {
  account: CurrentStaffAccount
  onChanged: () => void
}) {
  const { t } = useTranslation()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const changeMutation = useMutation({
    mutationFn: () => api.auth.changePassword({ currentPassword, newPassword }),
    onSuccess: () => onChanged(),
  })

  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword
  const tooShort = newPassword.length > 0 && newPassword.length < 8
  const canSubmit = currentPassword.length > 0 && newPassword.length >= 8 && newPassword === confirmPassword

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit || changeMutation.isPending) return
    changeMutation.mutate()
  }

  const serverError = changeMutation.isError
    ? changeMutation.error instanceof ApiError
      ? changeMutation.error.message
      : t('common.errorOccurred')
    : null

  return (
    <AuthBackground>
      <div className="w-full max-w-sm rounded-xl border border-white/15 bg-black/40 backdrop-blur-md shadow-elev-3 p-6 animate-[fade-in_900ms_ease]">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <h1 className="text-title-lg text-white">{t('auth.mustChangePasswordTitle')}</h1>
            <p className="text-body-sm text-white/70 mt-1">
              {t('auth.mustChangePasswordBody')} ({account.displayName})
            </p>
          </div>

          <TextField
            label={t('auth.currentPassword')}
            value={currentPassword}
            onChange={setCurrentPassword}
            type="password"
            autoComplete="current-password"
            autoFocus
            fullWidth
          />
          <TextField
            label={t('auth.newPassword')}
            value={newPassword}
            onChange={setNewPassword}
            type="password"
            autoComplete="new-password"
            error={tooShort ? t('auth.passwordTooShort') : undefined}
            fullWidth
          />
          <TextField
            label={t('auth.confirmNewPassword')}
            value={confirmPassword}
            onChange={setConfirmPassword}
            type="password"
            autoComplete="new-password"
            error={mismatch ? t('auth.passwordsDontMatch') : undefined}
            fullWidth
          />

          {serverError && (
            <p className="text-body-sm text-error" role="alert">
              {serverError}
            </p>
          )}

          <Button type="submit" variant="filled" fullWidth loading={changeMutation.isPending} disabled={!canSubmit}>
            {changeMutation.isPending ? t('auth.changingPassword') : t('auth.changePassword')}
          </Button>
        </form>
      </div>
    </AuthBackground>
  )
}
